import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { isEqual } from 'lodash';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { IconArrowUpRight, IconClock, IconHistory, IconRobot } from '@tabler/icons-react';
import { useAppSelector } from '@shared/store';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { useUserMeControllerUserMeQuery } from '@shared/services/apiService';
import { useWorkspaceScope } from './useWorkspaceScope';
import { MissionPlanner } from './MissionPlanner';
import { MissionsWorkspace } from './MissionsWorkspace';
import {
  useWorkspaceMissionQuery,
  useWorkspaceMissionsQuery,
  useWorkspaceOperationsQuery,
  type Mission,
  type MissionSpec,
  type WorkspaceOperation,
} from './workspaceApi';

const stateLabel = (mission: Mission) =>
  !mission.active_version_id ? 'Draft' : mission.paused ? 'Paused' : 'Published';
const pageSize = 50;
const presets = [
  {
    name: 'Weekday campaign summary',
    brief: 'Read the current saved campaign summary on weekdays.',
    cron: '0 9 * * 1-5',
  },
  {
    name: 'Weekly campaign summary',
    brief: 'Read the current saved campaign summary each week.',
    cron: '0 9 * * 1',
  },
  {
    name: 'On-demand campaign summary',
    brief: 'Read the current saved campaign summary when requested.',
  },
];
const missionSeed = (name: string, brief: string, cron?: string): MissionSpec => ({
  name,
  brief,
  trigger: cron
    ? {
        kind: 'schedule',
        cron,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      }
    : { kind: 'manual' },
  actions: [],
});

type Props = {
  overview: ReactNode;
  onDirtyChange: (dirty: boolean) => void;
  requestNavigation: (action: () => void) => void;
};
export const V5Dashboard = (props: Props) => {
  const scope = useWorkspaceScope();
  return <DashboardSession key={scope} {...props} />;
};

const DashboardSession = ({ overview, onDirtyChange, requestNavigation }: Props) => {
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const me = useUserMeControllerUserMeQuery();
  const workspace = useDesignWorkspace();
  const [brief, setBrief] = useState('');
  const [planner, setPlanner] = useState<{ key: string; seed: MissionSpec } | null>(null);
  const [plannerDirty, setPlannerDirty] = useState(false);
  const [editor, setEditor] = useState<{ key: string; draft?: MissionSpec; id?: string } | null>(
    null,
  );
  const [editorDirty, setEditorDirty] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [after, setAfter] = useState<string>();
  const [previousMissions, setPreviousMissions] = useState<Mission[]>([]);
  const [before, setBefore] = useState<string>();
  const [previousOperations, setPreviousOperations] = useState<WorkspaceOperation[]>([]);
  const missions = useWorkspaceMissionsQuery(after, {
    refetchOnMountOrArgChange: true,
    pollingInterval: after ? 0 : 15000,
    skipPollingIfUnfocused: true,
  });
  const operations = useWorkspaceOperationsQuery(
    { before },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: before ? 0 : 15000,
      skipPollingIfUnfocused: true,
    },
  );
  const missionRows = [
    ...new Map(
      [...previousMissions, ...(missions.currentData?.slice(0, pageSize) || [])].map((row) => [
        row.id,
        row,
      ]),
    ).values(),
  ];
  const operationRows = [
    ...new Map(
      [...previousOperations, ...(operations.currentData?.slice(0, pageSize) || [])].map((row) => [
        row.id,
        row,
      ]),
    ).values(),
  ];
  const missionsKnown = !missions.isError && !!missions.currentData;
  const operationsKnown = !operations.isError && !!operations.currentData;
  const canPlan =
    !!user &&
    user.role !== 'viewer' &&
    !me.isError &&
    me.currentData?.role !== 'viewer' &&
    me.currentData?.id === user.id &&
    me.currentData.companyId === user.companyId &&
    me.currentData.privileges.includes('campaign.manage');
  const name =
    me.currentData?.id === user?.id && me.currentData?.companyId === user?.companyId
      ? me.currentData?.firstName
      : undefined;
  const active = useRef(true);
  const plannerIdentity = useRef<string | null>(null);
  const allowed = useRef({
    canPlan,
    visible: !workspace.module || workspace.module === 'Dashboard',
  });
  allowed.current = { canPlan, visible: !workspace.module || workspace.module === 'Dashboard' };
  useLayoutEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      plannerIdentity.current = null;
    };
  }, []);
  const dirty = !!brief.trim() || plannerDirty || editorDirty;
  useLayoutEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  const plan = (seed: MissionSpec) => {
    if (!active.current || !allowed.current.canPlan || !allowed.current.visible) return;
    const key = crypto.randomUUID();
    plannerIdentity.current = key;
    setBrief('');
    setPlannerDirty(true);
    setPlanner({ key, seed });
  };
  const openMission = (id?: string) =>
    requestNavigation(() => {
      if (!active.current || !allowed.current.visible) return;
      plannerIdentity.current = null;
      setBrief('');
      setPlanner(null);
      setPlannerDirty(false);
      setEditorDirty(false);
      setEditor({ id, key: crypto.randomUUID() });
    });
  const failed = operationRows.filter(
    (row) => row.mission_id && ['failed', 'review'].includes(row.state),
  );
  const missionRuns = operationRows.filter((row) => row.mission_id);
  const published = missionRows.filter((row) => stateLabel(row) === 'Published').length;
  const paused = missionRows.filter((row) => stateLabel(row) === 'Paused').length;
  const value = (number: number, known: boolean) => (known ? number : 'Unavailable');
  if (editor)
    return (
      <MissionsWorkspace
        key={editor.key}
        initialDraft={editor.draft}
        initialMissionId={editor.id}
        onDirtyChange={setEditorDirty}
        requestNavigation={requestNavigation}
        onClose={() => {
          setEditor(null);
          setEditorDirty(false);
        }}
      />
    );
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0,1fr)',
          md: planner ? 'minmax(0,1fr) 440px' : 'minmax(0,1fr)',
        },
        gap: 3,
        alignItems: 'start',
      }}>
      <Stack component="section" aria-label="V5 mission dashboard" gap={4} sx={{ minWidth: 0 }}>
        <Box
          component="details"
          onToggle={(event) => setOverviewOpen(event.currentTarget.open)}
          sx={{ color: 'text.secondary' }}>
          <Box component="summary" sx={{ cursor: 'pointer', py: 1 }}>
            Workspace overview
          </Box>
          {overviewOpen && (
            <Stack gap={2} sx={{ mt: 2 }}>
              {overview}
              <Typography variant="caption">
                Native campaign, ongoing-call, billing and capacity figures. Open Your assistants or
                Review call history to inspect those saved resources.
              </Typography>
            </Stack>
          )}
        </Box>
        <Stack
          alignItems="center"
          gap={2}
          sx={{ maxWidth: 760, width: '100%', mx: 'auto', py: { xs: 2, md: 5 } }}>
          <MissionOrb />
          <Typography variant="body2" color="text.secondary">
            {name ? `Welcome back, ${name}.` : 'Welcome back.'}
          </Typography>
          <Typography component="h1" variant="h2" textAlign="center" sx={{ maxWidth: 580 }}>
            What would you like to make repeatable?
          </Typography>
          <Typography color="text.secondary" textAlign="center" sx={{ maxWidth: 520 }}>
            Start with an outcome. Review its explicit actions, timing and campaign context, then
            take the unsaved draft into Mission Studio.
          </Typography>
          <Paper
            component="form"
            variant="outlined"
            onSubmit={(event) => {
              event.preventDefault();
              if (brief.trim()) plan(missionSeed('', brief.trim()));
            }}
            sx={{ width: '100%', p: 2.5, borderRadius: 3 }}>
            <TextField
              label="Workspace mission brief"
              multiline
              minRows={3}
              maxRows={7}
              fullWidth
              value={brief}
              disabled={!canPlan || !!planner}
              onChange={(event) => setBrief(event.target.value)}
              inputProps={{ maxLength: 4000 }}
              placeholder="Read the current campaign summary every weekday…"
            />
            <Stack
              direction="row"
              gap={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Timing and campaign context come next
              </Typography>
              <Button
                type="submit"
                variant="contained"
                endIcon={<IconArrowUpRight size={16} />}
                disabled={!canPlan || !brief.trim() || !!planner}>
                Plan a mission
              </Button>
            </Stack>
          </Paper>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Guided planning. Your brief is not sent to AI; only the explicit reviewed native actions
            execute. Approval does not save or publish.
          </Typography>
          <Stack direction="row" gap={1} justifyContent="center" flexWrap="wrap">
            {presets.map((preset) => (
              <Button
                key={preset.name}
                variant="outlined"
                disabled={!canPlan || !!planner}
                onClick={() => plan(missionSeed(preset.name, preset.brief, preset.cron))}>
                {preset.name}
              </Button>
            ))}
          </Stack>
          {!canPlan && (
            <Alert severity="info">
              Current campaign management permission is required to prepare a new mission. Saved
              records remain available for inspection.
            </Alert>
          )}
        </Stack>
        <Stack direction="row" gap={2} flexWrap="wrap" justifyContent="center">
          <Button startIcon={<IconRobot size={17} />} onClick={() => workspace.open('Agents')}>
            Your assistants
          </Button>
          <Button startIcon={<IconClock size={17} />} onClick={() => openMission()}>
            Your missions
          </Button>
          <Button
            startIcon={<IconHistory size={17} />}
            onClick={() => workspace.open('Call history')}>
            Review call history
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between">
          <Box>
            <Typography variant="h5">Loaded mission snapshots</Typography>
            <Typography variant="body2" color="text.secondary">
              Each inventory page contains at most 50 missions. Inventory order is not
              chronological; these counts describe loaded records, not company totals or readiness.
            </Typography>
          </Box>
          <Button
            onClick={() => {
              setAfter(undefined);
              setPreviousMissions([]);
              if (!after) void missions.refetch();
            }}>
            Refresh missions
          </Button>
        </Stack>
        {missions.isError && (
          <Alert severity="error">
            The current mission page could not be loaded. Counts are unavailable; refresh or retry
            before treating this inventory as current.{' '}
            <Button onClick={() => void missions.refetch()}>Retry mission page</Button>
          </Alert>
        )}
        {missions.isFetching && (
          <CircularProgress size={22} aria-label="Loading mission snapshots" />
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,minmax(0,1fr))' },
            gap: 2,
          }}>
          {[
            ['Loaded missions', value(missionRows.length, missionsKnown)],
            ['Published in loaded set', value(published, missionsKnown)],
            ['Paused in loaded set', value(paused, missionsKnown)],
          ].map(([label, count]) => (
            <Paper key={label} variant="outlined" sx={{ p: 2.5 }}>
              <Typography color="text.secondary">{label}</Typography>
              <Typography variant="h3" aria-label={`${label}: ${count}`}>
                {count}
              </Typography>
            </Paper>
          ))}
        </Box>
        {missionsKnown && !missionRows.length && (
          <Alert severity="info">
            No missions are present in this loaded inventory. Plan a draft above to create one after
            review.
          </Alert>
        )}
        {missionsKnown && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2,minmax(0,1fr))' },
              gap: 2,
            }}>
            {missionRows.slice(0, 4).map((row) => (
              <MissionSnapshot key={row.id} mission={row} onOpen={() => openMission(row.id)} />
            ))}
          </Box>
        )}
        {missionsKnown && missionRows.length > 4 && (
          <Typography variant="caption" color="text.secondary">
            Showing 4 of {missionRows.length} loaded mission snapshots. Open Your missions to
            inspect the complete loaded catalog and edit a record.
          </Typography>
        )}
        {(missions.currentData?.length || 0) > pageSize && (
          <Button
            disabled={missions.isFetching || missions.isError}
            onClick={() => {
              const rows = missions.currentData?.slice(0, pageSize);
              if (!rows?.length) return;
              setPreviousMissions(missionRows);
              setAfter(rows.at(-1)!.id);
            }}>
            Load more mission snapshots
          </Button>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between">
          <Box>
            <Typography variant="h5">Attention from recorded runs</Typography>
            <Typography variant="body2" color="text.secondary">
              Only recorded failed or review-required mission runs are listed. This is not a
              readiness, credential, credit or provider-health assessment.
            </Typography>
          </Box>
          <Button
            onClick={() => {
              setBefore(undefined);
              setPreviousOperations([]);
              if (!before) void operations.refetch();
            }}>
            Refresh activity
          </Button>
        </Stack>
        {operations.isError && (
          <Alert severity="error">
            The current operation page could not be loaded. Recorded attention counts are
            unavailable.{' '}
            <Button onClick={() => void operations.refetch()}>Retry activity page</Button>
          </Alert>
        )}
        {operations.isFetching && (
          <CircularProgress size={22} aria-label="Loading recorded activity" />
        )}
        <Typography role="status">
          {operationsKnown
            ? `${operationRows.length} operation records loaded · ${missionRuns.length} mission runs · ${failed.length} failed or review-required records`
            : 'Recorded activity unavailable'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Each activity page contains at most 50 operations, including your assistant requests.
          Older pages are explicit snapshots. A later success does not erase an earlier recorded
          failure.
        </Typography>
        {operationsKnown && !failed.length && (
          <Alert severity="info">
            No failed or review-required mission runs in this loaded window.
          </Alert>
        )}
        {operationsKnown &&
          failed.slice(0, 5).map((row) => (
            <Paper key={row.id} variant="outlined" sx={{ p: 2.5 }}>
              <Stack gap={1}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    label={row.state}
                    color={row.state === 'review' ? 'warning' : 'error'}
                    size="small"
                  />
                  <Typography fontWeight={600}>
                    {missionRows.find((mission) => mission.id === row.mission_id)?.draft.name ||
                      `Mission ${row.mission_id}`}
                  </Typography>
                </Stack>
                <Typography variant="caption">
                  Run {row.id} · {new Date(row.created_at).toLocaleString()}
                </Typography>
                {row.error_code && (
                  <Typography variant="body2">
                    Recorded code: {row.error_code.replaceAll('_', ' ')}
                  </Typography>
                )}
                <Button sx={{ alignSelf: 'start' }} onClick={() => openMission(row.mission_id!)}>
                  Open mission
                </Button>
              </Stack>
            </Paper>
          ))}
        {operationsKnown && failed.length > 5 && (
          <Typography variant="caption">
            Showing 5 of {failed.length} matching recorded outcomes in the loaded window. Open the
            native mission run journal for full evidence.
          </Typography>
        )}
        {(operations.currentData?.length || 0) > pageSize && (
          <Button
            disabled={operations.isFetching || operations.isError}
            onClick={() => {
              const rows = operations.currentData?.slice(0, pageSize);
              if (!rows?.length) return;
              setPreviousOperations(operationRows);
              setBefore(rows.at(-1)!.id);
            }}>
            Load older activity
          </Button>
        )}
      </Stack>
      {planner && (
        <MissionPlanner
          key={planner.key}
          seed={planner.seed}
          canManage={canPlan}
          onDirtyChange={setPlannerDirty}
          onClose={() => {
            if (!active.current || plannerIdentity.current !== planner.key) return;
            requestNavigation(() => {
              if (
                !active.current ||
                !allowed.current.visible ||
                plannerIdentity.current !== planner.key
              )
                return;
              plannerIdentity.current = null;
              setPlanner(null);
              setPlannerDirty(false);
            });
          }}
          onApprove={(draft) => {
            if (
              !active.current ||
              !allowed.current.canPlan ||
              !allowed.current.visible ||
              plannerIdentity.current !== planner.key
            )
              return;
            plannerIdentity.current = null;
            setPlanner(null);
            setPlannerDirty(false);
            setEditorDirty(true);
            setEditor({ draft, key: crypto.randomUUID() });
          }}
        />
      )}
    </Box>
  );
};

const MissionSnapshot = ({ mission, onOpen }: { mission: Mission; onOpen: () => void }) => {
  const query = useWorkspaceMissionQuery(mission.id, { refetchOnMountOrArgChange: true });
  const detail =
    !query.isError && query.currentData?.mission.id === mission.id ? query.currentData : undefined;
  const current = detail?.mission ?? mission;
  const active = detail?.versions.find((version) => version.id === current.active_version_id);
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack gap={1.5}>
        <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{current.draft.name}</Typography>
          <Chip size="small" label={stateLabel(current)} variant="outlined" />
        </Stack>
        <Typography variant="caption">
          Mission {current.id} · saved revision {current.revision}
        </Typography>
        {current.active_version_id ? (
          active ? (
            <>
              <Typography>Published version {active.number}</Typography>
              {!isEqual(current.draft, active.spec) && (
                <Chip
                  label="Saved draft differs from published version"
                  size="small"
                  variant="outlined"
                />
              )}
            </>
          ) : (
            <Typography color="text.secondary">
              {query.isFetching ? 'Loading published snapshot…' : 'Published snapshot unavailable.'}
            </Typography>
          )
        ) : (
          <Typography color="text.secondary">No published version.</Typography>
        )}
        {current.next_due_at && (
          <Typography variant="body2">
            Recorded next occurrence: {new Date(current.next_due_at).toLocaleString()} (your local
            time)
          </Typography>
        )}
        {query.isError && (
          <Button onClick={() => void query.refetch()}>Retry mission snapshot</Button>
        )}
        <Button sx={{ alignSelf: 'start' }} onClick={onOpen}>
          Open {current.draft.name}
        </Button>
      </Stack>
    </Paper>
  );
};

const MissionOrb = () => {
  const theme = useTheme();
  const contrast = theme.palette.primary.contrastText;
  return (
    <Box
      component="svg"
      viewBox="0 0 180 180"
      aria-hidden="true"
      sx={{ width: 92, height: 92, color: 'primary.main', flexShrink: 0 }}>
      <ellipse cx="90" cy="159" rx="32" ry="5" fill="currentColor" opacity=".08" />
      <circle cx="90" cy="82" r="61" fill="currentColor" opacity=".055" />
      <path
        d="M34 104C19 81 29 48 53 31M127 30c27 15 38 45 26 71"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity=".2"
      />
      <rect x="46" y="43" width="88" height="87" rx="31" fill="currentColor" opacity=".12" />
      <rect x="51" y="39" width="78" height="87" rx="29" fill="currentColor" />
      <rect x="61" y="53" width="58" height="45" rx="20" fill={contrast} opacity=".94" />
      <path
        d="M70 77h7l5-10 8 21 8-20 5 9h7"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M78 111h24" stroke={contrast} strokeWidth="3" strokeLinecap="round" opacity=".75" />
      <path
        d="M59 126l-5 13m67-13 5 13M45 90l-9 9m99-9 9 9"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <path
        d="M24 38v10m-5-5h10M148 119v10m-5-5h10M142 24v6m-3-3h6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity=".55"
      />
      <circle cx="35" cy="127" r="2" fill="currentColor" opacity=".35" />
      <circle cx="151" cy="61" r="2" fill="currentColor" opacity=".35" />
    </Box>
  );
};
